// src/pages/LandingPage.jsx
// Shown at habitrak.io when user is not authenticated.
// Replaces the inline login gate in App.jsx.
import React from 'react';
import { useAppVersion } from '../version';
import { trackMarketingPageview } from '../analytics';

const t = {
  bg:        "#f5f0eb",
  surface:   "#fdf9f5",
  border:    "#ddd5c8",
  text:      "#1c1917",
  textSoft:  "#57534e",
  muted:     "#a8a29e",
  clay:      "#9a3412",
  clayLight: "#fff1ee",
  sage:      "#3d6b52",
  shadow:    "0 2px 12px rgba(28,20,12,0.09)",
};

const isNativeAndroid = typeof window !== "undefined" && window.Capacitor?.getPlatform?.() === "android" && window.Capacitor?.isNativePlatform?.();

export function LandingPage({ onLoginGoogle, onLoginApple, onLoginEmail, onResetPassword }) {
  const appVersion = useAppVersion();
  const [emailMode, setEmailMode] = React.useState(false);
  const [isNewUser, setIsNewUser]  = React.useState(false);
  const [email, setEmail]          = React.useState('');
  const [password, setPassword]    = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [showBeta, setShowBeta]     = React.useState(false);
  const [resetSending, setResetSending] = React.useState(false);
  const [resetMsg, setResetMsg]         = React.useState('');
  const [authError, setAuthError]       = React.useState('');

  React.useEffect(() => { trackMarketingPageview('/'); }, []);

  // Google/Apple sign-in on web previously had no error handling anywhere in
  // the chain — a popup failure (blocked, closed, misconfigured provider)
  // was a silent unhandled rejection. Now the real Firebase error (with its
  // .code) surfaces here so it's visible instead of invisible.
  const handleGoogleLogin = async () => {
    setAuthError('');
    try { await onLoginGoogle(); }
    catch (e) {
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') return;
      setAuthError(`Google sign-in failed${e?.code ? ` (${e.code})` : ''}${e?.message ? `: ${e.message}` : ''}`);
    }
  };
  const handleAppleLogin = async () => {
    setAuthError('');
    try { await onLoginApple(); }
    catch (e) {
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') return;
      setAuthError(`Apple sign-in failed${e?.code ? ` (${e.code})` : ''}${e?.message ? `: ${e.message}` : ''}`);
    }
  };

  const handleEmailSubmit = async () => {
    setEmailError('');
    setResetMsg('');
    try {
      await onLoginEmail(email, password, isNewUser);
    } catch (e) {
      const code = e?.code || '';
      if (!isNewUser && ['auth/user-not-found', 'auth/invalid-credential', 'auth/wrong-password'].includes(code)) {
        // Firebase's modern unified error code (auth/invalid-credential) covers
        // both "no such account" and "wrong password" for the same email —
        // it deliberately doesn't say which, so this message can't claim the
        // account doesn't exist (it might, with a different password).
        setEmailError('That email and password didn\'t match an account. Forgot your password? Or if you\'re new, tap "Create Account" above.');
      } else if (isNewUser && code === 'auth/email-already-in-use') {
        setEmailError('An account already exists for that email — tap "Sign In" above instead.');
      } else {
        setEmailError(e?.message || 'Sign-in failed');
      }
    }
  };

  const handleForgotPassword = async () => {
    setEmailError('');
    if (!email.trim()) { setEmailError('Enter your email above first, then tap "Forgot password?".'); return; }
    setResetSending(true);
    setResetMsg('');
    try {
      await onResetPassword(email.trim());
      setResetMsg("If an account exists for that email, we've sent a password reset link.");
    } catch (e) {
      // Deliberately vague on failure too — don't confirm/deny account
      // existence via this path either.
      setResetMsg("If an account exists for that email, we've sent a password reset link.");
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div style={{
      minHeight:      "100dvh",
      width:          "100%",
      maxWidth:       "100%",
      background:     t.bg,
      fontFamily:     "-apple-system,'Segoe UI',sans-serif",
      display:        "flex",
      flexDirection:  "column",
      alignItems:     "center",
      justifyContent: "space-between",
      padding:        "0 24px",
      boxSizing:      "border-box",
      overflowX:      "hidden",
    }}>

      {/* Hero */}
      <div style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", maxWidth: 420, paddingTop: 60, minWidth: 0 }}>
        <img src="/canntrak_icon_256.png" alt="CannaTrack" style={{ width: 72, height: 72, marginBottom: 16, borderRadius: 18 }} />

        <h1 style={{ fontSize: 36, fontWeight: 800, color: t.text, margin: "0 0 8px", letterSpacing: "-1px" }}>
          CannaTrack
        </h1>

        <p style={{ fontSize: 16, color: t.textSoft, margin: "0 0 20px", lineHeight: 1.6, fontWeight: 400 }}>
          Your Cannabis Journal.
        </p>

        {/* Sign in */}
        <div style={{ width: "100%", marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {!emailMode ? <>
            <button onClick={handleGoogleLogin} style={{ width:"100%", padding:"14px 0", borderRadius:12, background:"#9a3412", border:"none", fontSize:15, fontWeight:700, color:"#fff", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 1px 4px rgba(0,0,0,0.2)", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Sign in with Google
            </button>
            {onLoginApple && !isNativeAndroid && (
              <button onClick={handleAppleLogin} style={{ width:"100%", padding:"14px 0", borderRadius:12, background:"#000", border:"none", fontSize:15, fontWeight:700, color:"#fff", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 1px 4px rgba(0,0,0,0.2)", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 133.4-317.5 264.4-317.5 70.1 0 128.9 46.3 172.5 46.3 43.1 0 110.6-49 191.4-49 30.8 0 134.2 2.8 202.6 85.7zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/></svg>
                Sign in with Apple
              </button>
            )}
            {authError && <p style={{ fontSize:13, color:"#dc2626", margin:0, lineHeight:1.5 }}>{authError}</p>}
            <button onClick={() => setEmailMode(true)} style={{ width:"100%", padding:"14px 0", borderRadius:12, background:"transparent", border:`1px solid ${t.border}`, fontSize:15, fontWeight:600, color:t.text, cursor:"pointer", fontFamily:"inherit" }}>
              Sign in with Email
            </button>
          </> : <>
            <div style={{ display:"flex", gap:8 }}>
              {[[false,'Sign In'],[true,'Create Account']].map(([val,label]) => (
                <button key={label} onClick={() => { setIsNewUser(val); setEmailError(''); }} style={{
                  flex:1, padding:"11px 0", borderRadius:10, cursor:"pointer", fontFamily:"inherit", fontSize:14, fontWeight:700,
                  border:`1px solid ${isNewUser === val ? t.clay : t.border}`,
                  background: isNewUser === val ? t.clayLight : "transparent",
                  color: isNewUser === val ? t.clay : t.textSoft,
                }}>{label}</button>
              ))}
            </div>
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
              style={{ width:"100%", padding:"13px 14px", borderRadius:10, border:`1px solid ${t.border}`, fontSize:15, fontFamily:"inherit", background:t.surface, color:t.text, boxSizing:"border-box", outline:"none" }} />
            <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
              style={{ width:"100%", padding:"13px 14px", borderRadius:10, border:`1px solid ${t.border}`, fontSize:15, fontFamily:"inherit", background:t.surface, color:t.text, boxSizing:"border-box", outline:"none" }} />
            {emailError && <p style={{ fontSize:13, color:"#dc2626", margin:0, lineHeight:1.5 }}>{emailError}</p>}
            {resetMsg && <p style={{ fontSize:13, color:t.sage, margin:0, lineHeight:1.5 }}>{resetMsg}</p>}
            <button onClick={handleEmailSubmit} style={{ width:"100%", padding:"14px 0", borderRadius:12, background:"#9a3412", border:"none", fontSize:15, fontWeight:700, color:"#fff", cursor:"pointer", fontFamily:"inherit" }}>
              {isNewUser ? 'Create Account' : 'Sign In'}
            </button>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ cursor:"pointer", textDecoration:"underline", fontSize:14, color:t.muted }} onClick={() => { setEmailMode(false); setEmailError(''); setResetMsg(''); }}>
                Back
              </span>
              {!isNewUser && (
                <span style={{ cursor: resetSending ? "default" : "pointer", textDecoration:"underline", fontSize:14, color: resetSending ? t.muted : t.clay, opacity: resetSending ? 0.6 : 1 }} onClick={resetSending ? undefined : handleForgotPassword}>
                  {resetSending ? "Sending…" : "Forgot password?"}
                </span>
              )}
            </div>
          </>}
        </div>

        <p style={{ fontSize: 12, color: t.muted, margin: "0 0 8px", lineHeight: 1.6 }}>
          Private by default. No tracking.
        </p>

        <p style={{ fontSize: 11, color: t.muted, margin: "0 0 32px" }}>
          By signing in you agree to our{" "}
          <a href="/terms"   style={{ color: t.clay, textDecoration: "underline" }}>Terms of Service</a>
          {" "}and{" "}
          <a href="/privacy" style={{ color: t.clay, textDecoration: "underline" }}>Privacy Policy</a>.
        </p>

        {/* App availability callout */}
        <div style={{ width: "100%", marginBottom: 32, display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 12 }}>
          <a href="https://apps.apple.com/us/app/cannatrak/id6786504265" target="_blank" rel="noopener noreferrer" style={{ display: "flex" }}>
            <img src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk.svg" alt="Download on the App Store" style={{ height: 48, width: "auto" }} />
          </a>
          <button onClick={() => setShowBeta(true)} style={{ height: 48, padding: "0 16px", borderRadius: 10, background: "transparent", border: `1px solid ${t.border}`, fontSize: 14, fontWeight: 700, color: t.text, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
            Request Play Store Beta →
          </button>
        </div>

        {/* Value props */}
        <div style={{ width: "100%", marginBottom: 36, display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            ["📸", "Snap a label, done",         "Take a photo of any product package — CannaTrack reads the details for you. No lab report needed."],
            ["🌿", "Track your stash",          "Log what you use, rate it, and never forget a winner."],
            ["🏪", "Dispensary tracking",        "Track purchases from your local dispensaries — and go back for more."],
            ["💡", "Understand what you like",  "Correlate your stash to terpenes, potency, and effects. Know why something works for you. Optional: link a lab report (COA) for verified numbers."],
            ["🔒", "Private by default",         "Your data stays on your device. Cloud sync is optional. Delete everything with one tap."],
          ].map(([icon, title, desc]) => (
            <div key={title} style={{
              display:       "flex",
              alignItems:    "flex-start",
              width:         "100%",
              gap:           14,
              background:    t.surface,
              border:        `1px solid ${t.border}`,
              borderRadius:  12,
              padding:       "14px 16px",
              textAlign:     "left",
              boxShadow:     t.shadow,
              boxSizing:     "border-box",
              minWidth:      0,
            }}>
              <span style={{ fontSize: 22, flexShrink: 0, marginTop: 1 }}>{icon}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: t.text, marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 13, color: t.muted, lineHeight: 1.5, overflowWrap: "anywhere" }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer style={{ padding: "24px 0", textAlign: "center" }}>
        <p style={{ fontSize: 12, color: t.muted, margin: "0 0 8px" }}>
          <a href="/faq.html" style={{ color: t.clay, textDecoration: "none", fontWeight: 500 }}>FAQ</a>
          {" · "}
          <a href="/privacy" style={{ color: t.muted, textDecoration: "none" }}>Privacy</a>
          {" · "}
          <a href="/terms" style={{ color: t.muted, textDecoration: "none" }}>Terms</a>
          {" · "}
          <a href="https://instagram.com/cannatrak.app" target="_blank" rel="noopener noreferrer" style={{ color: t.muted, textDecoration: "none" }}>Instagram @cannatrak.app</a>
        </p>
        <p style={{ fontSize: 11, color: t.muted, margin: 0 }}>
          © 2026 CannaTrack · Privacy First · Always
        </p>
        <p style={{ fontSize: 11, color: t.muted, margin: "6px 0 0" }}>
          Version {appVersion}
        </p>
      </footer>

      {showBeta && <BetaSignupModal onClose={() => setShowBeta(false)} />}
    </div>
  );
}

// ============================================================
// Beta signup modal — email + App Store preference, posts to /api/beta-signup
// ============================================================

function BetaSignupModal({ onClose }) {
  const [betaEmail, setBetaEmail]       = React.useState('');
  const platform = 'android';
  const [sending, setSending]           = React.useState(false);
  const [sent, setSent]                 = React.useState(false);
  const [error, setError]               = React.useState('');

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSubmit = async () => {
    setError('');
    if (!EMAIL_RE.test(betaEmail.trim())) { setError('Enter a valid email address'); return; }
    setSending(true);
    try {
      const res = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: betaEmail.trim(), platform }),
      });
      if (res.ok) {
        setSent(true);
      } else {
        let message = 'Something went wrong - try again';
        try {
          const data = await res.json();
          if (data && typeof data.error === 'string' && data.error.trim()) {
            message = data.error;
          }
        } catch {
          // Keep generic fallback if error payload is not JSON.
        }
        if (message.length > 160) {
          message = 'Signup saved, but notification is temporarily unavailable. Please try again soon.';
        }
        setError(message);
      }
    } catch {
      setError('Something went wrong - try again');
    } finally {
      setSending(false);
    }
  };

  const inputStyle = { width: "100%", padding: "13px 14px", borderRadius: 10, border: `1px solid ${t.border}`, fontSize: 15, fontFamily: "inherit", background: t.bg, color: t.text, boxSizing: "border-box", outline: "none" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,20,12,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: 440, background: t.surface, borderRadius: "20px 20px 0 0", padding: "24px 24px calc(24px + env(safe-area-inset-bottom))", boxSizing: "border-box", boxShadow: t.shadow }}>
        {!sent ? <>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: "0 0 6px" }}>Request Play Store Beta</h2>
          <p style={{ fontSize: 14, color: t.textSoft, margin: "0 0 20px", lineHeight: 1.5 }}>
            Leave your email and we'll send you an invite as soon as a Play Store beta slot opens up.
          </p>

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }}>Email</label>
            <input type="email" placeholder="your@email.com" value={betaEmail} onChange={e => setBetaEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} style={inputStyle} />
          </div>

          {error && <p style={{ fontSize: 13, color: "#dc2626", margin: "0 0 12px" }}>{error}</p>}

          <button onClick={handleSubmit} disabled={sending} style={{ width: "100%", padding: "14px 0", borderRadius: 12, background: t.clay, border: "none", fontSize: 15, fontWeight: 700, color: "#fff", cursor: sending ? "default" : "pointer", fontFamily: "inherit", opacity: sending ? 0.7 : 1 }}>
            {sending ? "Sending…" : "Request Beta Access"}
          </button>
          <button onClick={onClose} style={{ width: "100%", padding: "12px 0", marginTop: 8, borderRadius: 12, background: "transparent", border: "none", fontSize: 14, fontWeight: 600, color: t.muted, cursor: "pointer", fontFamily: "inherit" }}>
            Not now
          </button>
        </> : <>
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: "0 0 6px" }}>You're on the list</h2>
            <p style={{ fontSize: 14, color: t.textSoft, margin: "0 0 20px" }}>We'll email you an invite as soon as a Play Store beta slot opens up.</p>
            <button onClick={onClose} style={{ width: "100%", padding: "13px 0", borderRadius: 12, background: t.sage, border: "none", fontSize: 15, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
              Done
            </button>
          </div>
        </>}
      </div>
    </div>
  );
}


// ============================================================
// src/pages/PrivacyPolicy.jsx
// ============================================================

export function PrivacyPolicy() {
  React.useEffect(() => { trackMarketingPageview('/privacy'); }, []);
  return (
    <LegalPage title="Privacy Policy" updated="May 2026">

      <Section title="The short version">
        <p>We don't know who you are. We know what you do — and only to make you smarter about it. No name, no email, no phone number is ever stored in our database.</p>
      </Section>

      <Section title="What we collect">
        <p>When you sign in with Google or Apple, we receive a unique identifier from them. We immediately convert it into an anonymous internal ID using a one-way hash. We never store the original identifier, your name, or your email address.</p>
        <p>We collect:</p>
        <ul>
          <li>Your sessions, intake logs, check-ins, and stash entries — linked only to your anonymous ID</li>
          <li>Optional: age range (5-year bands), sex, and US state — all voluntary, never required</li>
          <li>App version and timezone — for accurate time display</li>
          <li>Device type — for technical support and aggregate analytics only</li>
        </ul>
        <p>We do not collect: your name, email address, phone number, precise location, IP address (beyond transient infrastructure logs), or any other personally identifying information.</p>
      </Section>

      <Section title="The honest caveats">
        <p>No system is perfectly anonymous. Here's what we can't fully control:</p>
        <ul>
          <li><strong>Firebase Authentication</strong> — our authentication provider — knows you signed in via Google or Apple. We never see this information, but Google does, subject to their privacy policy.</li>
          <li><strong>Azure infrastructure</strong> transiently logs IP addresses at the network level. We do not log or store them in our application.</li>
          <li>Behavioral patterns, in theory, can sometimes re-identify individuals even without explicit PII. This is extremely unlikely at our scale and we take no steps to attempt it.</li>
        </ul>
      </Section>

      <Section title="How we use your data">
        <p>Your data is used for one purpose: to show you insights about your own habits, relative to your own goals. We do not sell it, share it with advertisers, or use it for targeting of any kind.</p>
        <p>We may use anonymized, aggregated data (minimum cohort of 5 users) to understand how the product is used overall. No individual's data is ever surfaced in aggregate analytics.</p>
      </Section>

      <Section title="Data retention">
        <p>We tier users by activity level and apply different retention rules on account deletion:</p>
        <ul>
          <li><strong>Transient / Casual users</strong> — all data purged immediately on deletion</li>
          <li><strong>Established / Core users</strong> — individual records deleted, anonymized aggregate contributions may be retained</li>
        </ul>
        <p>This is disclosed here and in the app. You can always use the Quick Delete option to remove all your data instantly.</p>
      </Section>

      <Section title="Your rights">
        <p>You can delete your account and all associated data at any time from the Settings tab. Deletion is immediate and irreversible for individual records.</p>
        <p>Because we store no PII, we cannot fulfill traditional "right to access" requests by name or email — we have no way to look you up. Your anonymous ID is your account.</p>
      </Section>

      <Section title="Children">
        <p>CannaTrack is not intended for users under 21. We do not knowingly collect data from anyone under 21.</p>
      </Section>

      <Section title="Contact">
        <p>Questions? Use the feedback form in the app. We read every message.</p>
      </Section>

    </LegalPage>
  );
}


// ============================================================
// src/pages/TermsOfService.jsx
// ============================================================

export function TermsOfService() {
  React.useEffect(() => { trackMarketingPageview('/terms'); }, []);
  return (
    <LegalPage title="Terms of Service" updated="May 2026">

      <Section title="Who this is for">
        <p>CannaTrack is intended for adults 21 and older. By using this app you confirm you are at least 21 years of age, or the legal age for your jurisdiction, whichever is higher.</p>
      </Section>

      <Section title="What CannaTrack is">
        <p>CannaTrack is a personal habit tracking tool. It helps you log, measure, and reflect on your own behavior — whatever your goal is. It is not medical advice, legal advice, or a treatment program.</p>
      </Section>

      <Section title="Cannabis and legality">
        <p>Cannabis laws vary by jurisdiction. CannaTrack does not encourage, facilitate, or condone any illegal activity. You are solely responsible for understanding and complying with the laws in your area.</p>
      </Section>

      <Section title="No warranties">
        <p>CannaTrack is provided as-is. We make no guarantees about accuracy, availability, or fitness for any particular purpose. THC absorption estimates are approximations based on published research — individual results vary significantly.</p>
      </Section>

      <Section title="Limitation of liability">
        <p>To the maximum extent permitted by law, CannaTrack and its operators are not liable for any damages arising from your use of the app, including decisions made based on data shown in the app.</p>
      </Section>

      <Section title="Your data">
        <p>You own your data. We are custodians of it, subject to our Privacy Policy. You can delete it at any time.</p>
      </Section>

      <Section title="Changes">
        <p>We may update these terms. Material changes will be communicated in the app. Continued use after changes constitutes acceptance.</p>
      </Section>

      <Section title="Contact">
        <p>Questions? Use the feedback form in the app.</p>
      </Section>

    </LegalPage>
  );
}


// ============================================================
// Shared layout components (used by both legal pages)
// ============================================================

function LegalPage({ title, updated, children }) {
  return (
    <div style={{
      minHeight:  "100vh",
      background: "#f5f0eb",
      fontFamily: "-apple-system,'Segoe UI',sans-serif",
      color:      "#1c1917",
    }}>
      {/* Header */}
      <div style={{
        background:   "#fdf9f5",
        borderBottom: "1px solid #ddd5c8",
        padding:      "16px 20px",
        display:      "flex",
        alignItems:   "center",
        gap:          12,
      }}>
        <a href="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
          <img src="/canntrak_icon_256.png" alt="CannaTrack" style={{ width: 24, height: 24, borderRadius: 6 }} />
        </a>
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1c1917" }}>CannaTrack</span>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px 60px" }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.5px" }}>{title}</h1>
        <p style={{ fontSize: 12, color: "#a8a29e", margin: "0 0 32px" }}>Last updated: {updated}</p>
        {children}
      </div>

      {/* Footer */}
      <div style={{ textAlign: "center", padding: "0 0 32px", fontSize: 11, color: "#a8a29e" }}>
        © 2026 CannaTrack · <a href="/privacy" style={{ color: "#a8a29e" }}>Privacy</a> · <a href="/terms" style={{ color: "#a8a29e" }}>Terms</a>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: "#1c1917", margin: "0 0 10px" }}>{title}</h2>
      <div style={{ fontSize: 14, color: "#57534e", lineHeight: 1.75 }}>
        {children}
      </div>
    </div>
  );
}


// ============================================================
// App.jsx PATCH — add page routing
// ============================================================
// Add these imports at the top of App.jsx:
//
//   import { LandingPage }    from "./pages/LandingPage";
//   import { PrivacyPolicy }  from "./pages/PrivacyPolicy";
//   import { TermsOfService } from "./pages/TermsOfService";
//
//
// Replace the existing auth gate block in HabitTrack() with:
//
//   const { isAuthenticated, login, logout } = useAuth();
//
//   if (window.location.pathname === "/auth/callback") return <AuthCallback />;
//   if (window.location.pathname === "/privacy")       return <PrivacyPolicy />;
//   if (window.location.pathname === "/terms")         return <TermsOfService />;
//   if (!isAuthenticated)                              return <LandingPage onLogin={login} />;
//
// That's it — four lines replaces the old inline login gate.
// ============================================================
